/**
 * BSGOTECH Corporate Manual — 12-volume index page.
 *
 * Every volume is served as raw markdown at /downloads/manual/VOLxx_*.md so
 * it can be read directly, downloaded, or ingested by an AI training pipeline.
 * DE / EN / PT for shell copy; volume titles stay in English (source of truth).
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, BookOpen, Download } from "lucide-react";
import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  surface: "rgba(255,255,255,0.03)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
};

const VOLUMES = [
  { n: "01", file: "VOL01_Company_Foundation.md",     title: { en: "Company Foundation",       de: "Unternehmensgrundlage",         pt: "Fundação da Empresa" },           focus: { en: "Mission, vision, values, philosophy, DNA",                     de: "Mission, Vision, Werte, Philosophie, DNA",                        pt: "Missão, visão, valores, filosofia, ADN" } },
  { n: "02", file: "VOL02_Business_Architecture.md",  title: { en: "Business Architecture",    de: "Geschäftsarchitektur",          pt: "Arquitectura de Negócio" },       focus: { en: "Corporate structure, brand hierarchy, revenue model",         de: "Unternehmensstruktur, Markenhierarchie, Erlösmodell",              pt: "Estrutura corporativa, hierarquia de marca, modelo de receita" } },
  { n: "03", file: "VOL03_Website_Manual.md",         title: { en: "Website Manual",           de: "Website-Handbuch",              pt: "Manual do Website" },             focus: { en: "Every page, every section, every rule",                       de: "Jede Seite, jeder Abschnitt, jede Regel",                          pt: "Cada página, cada secção, cada regra" } },
  { n: "04", file: "VOL04_Platform_Documentation.md", title: { en: "Platform Documentation",   de: "Plattform-Dokumentation",       pt: "Documentação das Plataformas" },  focus: { en: "BSGOTECH · NeuNexus · RENALIX · NeuGreen",                    de: "BSGOTECH · NeuNexus · RENALIX · NeuGreen",                        pt: "BSGOTECH · NeuNexus · RENALIX · NeuGreen" } },
  { n: "05", file: "VOL05_Technology.md",             title: { en: "Technology",               de: "Technologie",                   pt: "Tecnologia" },                    focus: { en: "AI · Cloud · Data · IoT · Edge · Twin · Vision · Energy · Health", de: "KI · Cloud · Daten · IoT · Edge · Twin · Vision · Energie · Health", pt: "IA · Cloud · Dados · IoT · Edge · Twin · Vision · Energia · Saúde" } },
  { n: "06", file: "VOL06_Business_Processes.md",     title: { en: "Business Processes",       de: "Geschäftsprozesse",             pt: "Processos de Negócio" },          focus: { en: "Sales, projects, partners, meetings, decisions",              de: "Vertrieb, Projekte, Partner, Meetings, Entscheidungen",            pt: "Vendas, projectos, parceiros, reuniões, decisões" } },
  { n: "07", file: "VOL07_Strategic_Partnerships.md", title: { en: "Strategic Partnerships",   de: "Strategische Partnerschaften",  pt: "Parcerias Estratégicas" },        focus: { en: "Selection, lifecycle, non-negotiables",                       de: "Auswahl, Lebenszyklus, Non-Negotiables",                           pt: "Selecção, ciclo de vida, não-negociáveis" } },
  { n: "08", file: "VOL08_Sales_Playbook.md",         title: { en: "Sales Playbook",           de: "Sales Playbook",                pt: "Manual de Vendas" },              focus: { en: "Pitches 10 sec → 10 min + verticals",                         de: "Pitches 10 s → 10 min + Vertikale",                                pt: "Pitches 10 seg → 10 min + verticais" } },
  { n: "09", file: "VOL09_Founder_Handbook.md",       title: { en: "Founder Handbook",         de: "Founder-Handbuch",              pt: "Manual do Fundador" },            focus: { en: "Communication, negotiation, leadership, etiquette",           de: "Kommunikation, Verhandlung, Leadership, Etikette",                 pt: "Comunicação, negociação, liderança, etiqueta" } },
  { n: "10", file: "VOL10_Knowledge_Base_FAQ.md",     title: { en: "Knowledge Base (FAQ)",     de: "Wissensdatenbank (FAQ)",        pt: "Base de Conhecimento (FAQ)" },    focus: { en: "500+ enterprise Q&A across 17 categories",                    de: "500+ Enterprise-Q&A in 17 Kategorien",                             pt: "500+ Q&A empresariais em 17 categorias" } },
  { n: "11", file: "VOL11_Website_Content_Library.md", title: { en: "Website Content Library", de: "Website-Inhaltsbibliothek",     pt: "Biblioteca de Conteúdos do Website" }, focus: { en: "Canonical copy for hero, mission, footer, SEO",           de: "Kanonische Texte für Hero, Mission, Footer, SEO",                  pt: "Textos canónicos para hero, missão, footer, SEO" } },
  { n: "12", file: "VOL12_AI_Knowledge_Base.md",      title: { en: "AI Knowledge Base",        de: "KI-Wissensdatenbank",           pt: "Base de Conhecimento para IA" },  focus: { en: "Structured entries ready for AI ingestion",                   de: "Strukturierte Einträge für KI-Ingestion",                          pt: "Entradas estruturadas para ingestão por IA" } },
];

const COPY = {
  en: {
    docTitle: "Corporate Manual · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Corporate Manual · 12 Volumes",
    title: "The official strategic, technical and operational reference for BSGOTECH.",
    subtitle: "Every volume is a living document. Each volume is designed to grow without breaking the four-line positioning contract.",
    volLabel: "Vol.",
    open: "Open",
    download: "Download .md",
    contract: "Consistency contract",
    contractLines: [
      "BSGOTECH develops intelligent digital ecosystems.",
      "NeuNexus integrates technologies into complete solutions.",
      "RENALIX delivers AI-powered healthcare innovation.",
      "NeuGreen Energy delivers intelligent renewable energy solutions.",
    ],
  },
  de: {
    docTitle: "Corporate Manual · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Corporate Manual · 12 Bände",
    title: "Die offizielle strategische, technische und operative Referenz für BSGOTECH.",
    subtitle: "Jeder Band ist ein lebendes Dokument und lässt sich beliebig erweitern, ohne die vier Positionierungssätze zu brechen.",
    volLabel: "Bd.",
    open: "Öffnen",
    download: ".md herunterladen",
    contract: "Konsistenzvertrag",
    contractLines: [
      "BSGOTECH entwickelt intelligente digitale Ökosysteme.",
      "NeuNexus integriert Technologien zu vollständigen Lösungen.",
      "RENALIX liefert KI-gestützte Innovation für das Gesundheitswesen.",
      "NeuGreen Energy liefert intelligente Lösungen für erneuerbare Energien.",
    ],
  },
  pt: {
    docTitle: "Manual Corporativo · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Manual Corporativo · 12 Volumes",
    title: "A referência estratégica, técnica e operacional oficial da BSGOTECH.",
    subtitle: "Cada volume é um documento vivo, desenhado para crescer sem quebrar o contrato das quatro linhas de posicionamento.",
    volLabel: "Vol.",
    open: "Abrir",
    download: "Descarregar .md",
    contract: "Contrato de consistência",
    contractLines: [
      "A BSGOTECH desenvolve ecossistemas digitais inteligentes.",
      "A NeuNexus integra tecnologias em soluções completas.",
      "A RENALIX entrega inovação em saúde potenciada por IA.",
      "A NeuGreen Energy entrega soluções inteligentes de energia renovável.",
    ],
  },
};

const useM = () => {
  const { lang } = React.useContext(LangCtx);
  return { lang, t: COPY[lang] || COPY.en };
};

export default function ManualIndex() {
  const { lang, t } = useM();
  React.useEffect(() => { document.title = t.docTitle; }, [t.docTitle]);

  return (
    <div className="relative min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-manual">
      <LanguageSwitcher />

      {/* Top bar */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(8,11,18,0.85)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }} data-testid="manual-back-home">
            <ChevronRight size={13} className="rotate-180" />
            <span className="uppercase tracking-[0.28em]">{t.back}</span>
          </Link>
          <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            <BookOpen size={12} /> {t.eyebrow}
          </div>
          <div />
        </div>
      </div>

      {/* Header */}
      <header className="border-b overflow-hidden" style={{ borderColor: P.border }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>{t.eyebrow}</div>
            <h1 className="mt-5 font-semibold tracking-tight text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05]"
              style={{ color: "#fff", letterSpacing: "-0.015em" }}>
              {t.title}
            </h1>
            <p className="mt-5 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
              {t.subtitle}
            </p>
          </div>
          {/* Editorial cover — Nano Banana render */}
          <div className="relative" data-testid="manual-cover-visual">
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
              background: `radial-gradient(circle at 50% 50%, ${P.green}22 0%, transparent 65%)`,
              filter: "blur(24px)",
            }} />
            <img
              src="/bsgotech/strategic/manual-cover.jpg"
              alt="BSGOTECH Corporate Manual"
              className="relative w-full h-auto rounded-xl"
              style={{ boxShadow: `0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px ${P.border}` }}
              loading="lazy"
            />
          </div>
        </div>
      </header>

      {/* Volumes grid */}
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14 space-y-3">
        {VOLUMES.map((v) => {
          // Serve per-language file when available (.de.md / .pt.md), otherwise fall back to English source.
          const suffix = lang === "en" ? "" : `.${lang}`;
          const filename = v.file.replace(/\.md$/, `${suffix}.md`);
          const href = `/downloads/manual/${filename}`;
          return (
            <div
              key={v.n}
              data-testid={`manual-volume-${v.n}`}
              className="rounded-xl overflow-hidden grid md:grid-cols-[80px_1fr_auto] gap-0 transition hover:border-white/25"
              style={{ background: P.surface, border: `1px solid ${P.border}` }}
            >
              <div className="flex items-center justify-center py-6 md:py-0 border-b md:border-b-0 md:border-r"
                style={{ borderColor: P.border, background: `linear-gradient(135deg, ${P.blue}0f 0%, ${P.bg2} 100%)` }}>
                <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>{t.volLabel}</div>
                <div className="ml-2 font-mono text-[22px] font-semibold" style={{ color: "#fff" }}>{v.n}</div>
              </div>
              <div className="px-6 py-5">
                <div className="text-[16px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>{v.title[lang] || v.title.en}</div>
                <div className="mt-1 text-[12.5px]" style={{ color: P.dim }}>{v.focus[lang] || v.focus.en}</div>
              </div>
              <div className="px-6 py-5 flex md:justify-end items-center gap-3">
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`manual-open-${v.n}`}
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.2em] transition hover:text-white"
                  style={{ color: P.green }}
                >
                  {t.open} <ArrowUpRight size={12} />
                </a>
                <a
                  href={href}
                  download
                  data-testid={`manual-download-${v.n}`}
                  className="inline-flex items-center gap-1.5 text-[11.5px] transition hover:text-white"
                  style={{ color: P.silver }}
                >
                  <Download size={12} /> {t.download}
                </a>
              </div>
            </div>
          );
        })}

        {/* Consistency contract */}
        <div className="mt-10 rounded-xl p-6 lg:p-8" style={{ background: `linear-gradient(135deg, ${P.green}0a 0%, ${P.bg2} 55%)`, border: `1px solid ${P.green}44` }}>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>{t.contract}</div>
          <ul className="mt-4 space-y-1.5 text-[13.5px] leading-relaxed" style={{ color: P.ink }}>
            {t.contractLines.map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </main>
    </div>
  );
}
